import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import DateField from './DateField';
import type { GymMemberPayload } from '../../types/gym-member';

const today = new Date();
/** Wide enough for any living member, narrow enough for a usable year list. */
const OLDEST_BIRTH_DATE = new Date(1920, 0, 1);
// A certificate is typically annual; allow a couple of years either side so an
// expired one can still be recorded and a renewal entered ahead of time.
const OLDEST_CERTIFICATE = new Date(today.getFullYear() - 5, 0, 1);
const LATEST_CERTIFICATE = new Date(today.getFullYear() + 5, 11, 31);

interface GymMemberFieldsProps {
  form: GymMemberPayload;
  onChange: (form: GymMemberPayload) => void;
  /** Prefix for the input ids, so two forms can coexist on a page. */
  idPrefix?: string;
}

/**
 * The gym's own record of a member. Shared by the roster dialog and the
 * standalone edit page so the two never drift apart.
 *
 * These fields belong to the gym: editing them never touches the linked
 * climber's DivePeaks profile.
 */
export default function GymMemberFields({ form, onChange, idPrefix = 'member' }: GymMemberFieldsProps) {
  const { t } = useTranslation();

  const setField = (field: keyof GymMemberPayload, value: string) =>
    onChange({ ...form, [field]: value });

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-name`}>{t('gymAdmin.members.fields.name')}</Label>
          <Input
            id={`${idPrefix}-name`}
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-surname`}>{t('gymAdmin.members.fields.surname')}</Label>
          <Input
            id={`${idPrefix}-surname`}
            value={form.surname ?? ''}
            onChange={(e) => setField('surname', e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-email`}>{t('gymAdmin.members.fields.email')}</Label>
          <Input
            id={`${idPrefix}-email`}
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setField('email', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-phone`}>{t('gymAdmin.members.fields.phone')}</Label>
          <Input
            id={`${idPrefix}-phone`}
            value={form.phone ?? ''}
            onChange={(e) => setField('phone', e.target.value)}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <DateField
          id={`${idPrefix}-birth-date`}
          label={t('gymAdmin.members.fields.birthDate')}
          value={form.birth_date ?? ''}
          onChange={(value) => setField('birth_date', value)}
          // Year dropdown: nobody wants to page back three decades a month at a time.
          captionLayout="dropdown"
          startMonth={OLDEST_BIRTH_DATE}
          endMonth={today}
        />
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-fiscal-code`}>{t('gymAdmin.members.fields.fiscalCode')}</Label>
          <Input
            id={`${idPrefix}-fiscal-code`}
            value={form.fiscal_code ?? ''}
            onChange={(e) => setField('fiscal_code', e.target.value.toUpperCase())}
          />
        </div>
      </div>

      <DateField
        id={`${idPrefix}-certificate`}
        label={t('gymAdmin.members.fields.medicalCertificate')}
        value={form.medical_certificate_expires_at ?? ''}
        onChange={(value) => setField('medical_certificate_expires_at', value)}
        captionLayout="dropdown"
        startMonth={OLDEST_CERTIFICATE}
        endMonth={LATEST_CERTIFICATE}
      />

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-notes`}>{t('gymAdmin.members.fields.notes')}</Label>
        <Textarea
          id={`${idPrefix}-notes`}
          rows={3}
          value={form.notes ?? ''}
          onChange={(e) => setField('notes', e.target.value)}
        />
      </div>
    </div>
  );
}
